// PARTNER-PORTÁL · Üzenetek és meghívók — a terv szerint
// (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9).
//
// SZERKEZET: „Közelgő meghívóim" (kártyák RSVP-vel) → „Bejövő üzenetek" (lista, olvasottság-jelöléssel) →
// a megnyitott üzenet beúszó panelben (teljes törzs + mellékletek).
//
// KÉT DOLOG, AMIT A FELÜLET KIMOND (nem csendben elhallgat):
//  • ha a modul nem szolgálható ki (nincs gépi Odin-kredenciál), MAGYARÁZATOT mutat — nem üres listát,
//    mert az „nincs üzenete"-ként hazudna;
//  • a meghívón a válasz MINDIG módosítható (a terv „Válasz módosítása" gombja) — egy visszaigazolt
//    részvételt is le lehet mondani, amíg a megbeszélés el nem kezdődött.
import type { PortalMeetingRow, PortalMessageRow } from '@atlas/contracts';
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { MeetingRsvpBadge } from '@/components/portal/meeting-rsvp-badge';
import { MessageScopeBadge } from '@/components/portal/message-scope-badge';
import { PortalErrorState } from '@/components/portal/portal-error-state';
import { CardRow, CardRows } from '@/components/shared/card-rows';
import { EmptyState } from '@/components/shared/empty-state';
import { MetaDot } from '@/components/shared/meta-dot';
import { PersonChip } from '@/components/shared/person-chip';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { usePortal, usePortalHead } from '@/features/portal/portal-context';
import { api } from '@/lib/api';
import { formatDateTime, formatRelativeTime } from '@/lib/format-datetime';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  open: z.string().optional(),
  // OLVASÓ-OLDALI ARCHÍVUM nézet (`only` → csak a saját archiváltak) — linkelhető, F5-biztos
  archived: z.enum(['only']).optional(),
});

export const Route = createFileRoute('/portal/messages')({
  validateSearch: searchSchema,
  component: PortalMessagesPage,
});

function PortalMessagesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [openId, setOpenId] = useState<string | null>(search.open ?? null);

  usePortalHead({ title: t('portal.nav.messages'), subtitle: t('portal.messages.subtitle') }, [
    t('portal.nav.messages'),
  ]);

  const { accountId } = usePortal();
  const lang = i18n.language === 'en' ? 'en' : 'hu';
  // A FIÓK-SCOPE itt is hat, de CSAK a fiók-címzett üzenetekre: a személyes üzenet, az ügyfél-közlemény és a
  // MEGHÍVÓK (személy-scope) mindig látszanak — a szerver dönti el, nem a lap (lásd a szerződés-doksit).
  const archivedView = search.archived === 'only';
  const listKey = portalKeys.messageList(lang, accountId, archivedView);
  const q = useQuery({
    queryKey: listKey,
    queryFn: () =>
      api.portal.messages.query({
        query: {
          lang,
          ...(accountId !== null ? { accountId } : {}),
          ...(archivedView ? { archived: 'only' as const } : {}),
        },
      }),
  });
  /** Az „Archivált" nézet ki/be — az URL-be írva (a megnyitott üzenet közben bezárul). */
  const toggleArchivedView = (): void => {
    setOpenId(null);
    void navigate({
      to: '/portal/messages',
      search: archivedView ? {} : { archived: 'only' },
      replace: true,
    });
  };
  const body = q.data?.status === 200 ? q.data.body : undefined;
  const isForbidden = q.data?.status === 403;
  const isError = !q.isLoading && q.data !== undefined && q.data.status !== 200 && !isForbidden;

  const detailQ = useQuery({
    queryKey: portalKeys.messageDetail(openId ?? '', lang),
    queryFn: () =>
      api.portal.messageDetail.query({ params: { id: openId ?? '' }, query: { lang } }),
    enabled: openId !== null,
  });
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;
  const detailFailed =
    openId !== null &&
    !detailQ.isLoading &&
    (detailQ.isError || (detailQ.data !== undefined && detailQ.data.status !== 200));

  const markRead = useMutation({
    mutationFn: (id: string) => api.portal.messageRead.mutation({ params: { id } }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.messages() });
      // A KERET jelvénye is ebből él (olvasatlan üzenetek) — azt is frissítjük.
      void queryClient.invalidateQueries({ queryKey: ['portal', 'me'] });
    },
  });

  // MIND OLVASOTT — egy kérés, a szerver dönti el, mi látható és mi olvasatlan. A toast a TÉNYLEGES
  // darabszámot mondja: ha 0, azt is (nem hazudunk „kész"-t egy no-opra).
  const markAllRead = useMutation({
    mutationFn: () => api.portal.messagesReadAll.mutation({}),
    onSuccess: (res) => {
      if (res.status !== 200) {
        toast.error(t('toast:portal.error'));
        return;
      }
      toast.success(t('toast:portal.messagesAllRead', { count: res.body.updated }));
    },
    onError: () => toast.error(t('toast:portal.error')),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.messages() });
      void queryClient.invalidateQueries({ queryKey: portalKeys.all });
    },
  });

  // ARCHIVÁLÁS / VISSZAÁLLÍTÁS a megnyitott üzenetből — a SAJÁT nézetre hat (a többi címzettnél megmarad); utána a
  // panel bezárul, mert az üzenet kikerül az épp látott listából.
  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived
        ? api.portal.messageArchive.mutation({ params: { id } })
        : api.portal.messageUnarchive.mutation({ params: { id } }),
    onSuccess: (res, vars) => {
      if (res.status !== 200) {
        toast.error(t('toast:portal.error'));
        return;
      }
      toast.success(
        t(vars.archived ? 'toast:portal.messageArchived' : 'toast:portal.messageUnarchived'),
      );
      close();
    },
    onError: () => toast.error(t('toast:portal.error')),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.messages() });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'me'] });
    },
  });

  const rsvp = useMutation({
    mutationFn: (v: { id: string; status: 'pending' | 'confirmed' | 'declined' }) =>
      api.portal.meetingRsvp.mutation({ params: { id: v.id }, body: { status: v.status } }),
    onSuccess: (res, v) => {
      if (res.status !== 200) {
        toast.error(t('toast:portal.error'));
        return;
      }
      toast.success(
        v.status === 'confirmed'
          ? t('toast:portal.rsvpConfirmed')
          : v.status === 'declined'
            ? t('toast:portal.rsvpDeclined')
            : t('toast:portal.rsvpReset'),
      );
    },
    // A ts-rest a nem-2xx-et ELDOBJA, tehát a hálózati hiba és a szerződésen kívüli státusz IDE érkezik, nem
    // az `onSuccess`-be. Enélkül a résztvevő rákattint a „Részt veszek"-re, és SEMMI nem történik: se
    // visszajelzés, se hibaüzenet — a gomb halottnak látszik. (A testvér `markAllRead` ezt kezeli; itt
    // kimaradt.) A `markRead` SZÁNDÉKOSAN nem kap ilyet: az néma háttér-művelet, ami a következő
    // betöltéskor magától helyreáll.
    onError: () => toast.error(t('toast:portal.error')),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.messages() });
    },
  });

  // a megnyitás / bezárás az ARCHÍV-nézetet MEGTARTJA (különben a lap a bejövőre ugrana, és a panel a rossz
  // akciót — „Archiválás" a „Visszaállítás" helyett — kínálná)
  const viewSearch = archivedView ? { archived: 'only' as const } : {};
  const open = (m: PortalMessageRow): void => {
    setOpenId(m.id);
    void navigate({ to: '/portal/messages', search: { ...viewSearch, open: m.id }, replace: true });
    if (m.unread) markRead.mutate(m.id);
  };
  const close = (): void => {
    setOpenId(null);
    void navigate({ to: '/portal/messages', search: viewSearch, replace: true });
  };

  if (isForbidden) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  if (isError) {
    return <PortalErrorState onRetry={() => void q.refetch()} />;
  }
  // A modul nem szolgálható ki ebben a környezetben — MAGYARÁZAT, nem üres lista.
  if (body?.unavailable === true) {
    return (
      <EmptyState
        icon={EnvelopeIcon}
        title={t('portal.messages.unavailable.title')}
        description={t('portal.messages.unavailable.desc')}
      />
    );
  }

  const pendingRsvp = (body?.meetings ?? []).filter((m) => m.rsvp === 'pending');

  const unreadCount = body?.unreadCount ?? 0;

  return (
    <div className="space-y-8">
      {/* KÖZELGŐ MEGBESZÉLÉSEIM — a lap ELSŐ blokkja. A „mind olvasott" akció korábban EGY SAJÁT SÁVBAN
          állt a lap tetején, jobbra igazítva: az a sáv csak helyet vett el (a lap tartalma egy gomb-sorral
          lejjebb kezdődött), és a művelet messze került attól, amire hat. A gomb átkerült a BEJÖVŐ
          ÜZENETEK címe mellé (user-döntés). */}
      <section className="space-y-3">
        <div className="flex items-center gap-2.5">
          <Text variant="title" as="h2">
            {t('portal.messages.meetingsTitle')}
          </Text>
          {pendingRsvp.length > 0 && (
            // IKONOS jelvény (user-lelet): a szemközti „N olvasatlan" boríték-ikonnal áll, ez viszont
            // csupasz szám volt — a két cím melletti jelvény nem ugyanazt a nyelvet beszélte.
            <Badge variant="warning" icon={<ClockIcon />}>
              {t('portal.messages.awaitingAnswer', { count: pendingRsvp.length })}
            </Badge>
          )}
        </div>

        {q.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (body?.meetings.length ?? 0) === 0 ? (
          <Card>
            <EmptyState
              inset
              icon={CalendarDaysIcon}
              title={t('portal.messages.noMeetings.title')}
              description={t('portal.messages.noMeetings.desc')}
            />
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(body?.meetings ?? []).map((m) => (
              <MeetingCard
                key={m.id}
                meeting={m}
                busy={rsvp.isPending}
                onRsvp={(status) => rsvp.mutate({ id: m.id, status })}
              />
            ))}
          </div>
        )}
      </section>

      {/* BEJÖVŐ ÜZENETEK */}
      <section className="space-y-3">
        <div className="flex items-center gap-2.5">
          <Text variant="title" as="h2">
            {archivedView ? t('portal.messages.archivedTitle') : t('portal.messages.inboxTitle')}
          </Text>
          {unreadCount > 0 && (
            <Badge variant="danger" icon={<EnvelopeIcon />}>
              {t('portal.messages.unreadCount', { count: unreadCount })}
            </Badge>
          )}
          {/* „MIND OLVASOTT" a CÍM MELLETT, jobbra igazítva (user-döntés): a művelet arra a listára hat,
              amelyiknek a fejlécében áll — a lap tetején, saját sávban ez a kapcsolat nem látszott. CSAK
              akkor jelenik meg, ha van mit jelölni: egy tiltott gomb itt zaj. */}
          <div className="ml-auto flex items-center gap-2">
            {!archivedView && unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                <CheckCircleIcon />
                {t('portal.messages.markAllRead')}
              </Button>
            )}
            {/* ARCHÍV-nézet kapcsoló (a belső üzenet-lista „Archivált" gombjának párja) */}
            <Button
              variant="outline"
              size="sm"
              aria-pressed={archivedView}
              onClick={toggleArchivedView}
              className={archivedView ? 'bg-accent text-foreground' : undefined}
            >
              <ArchiveBoxIcon />
              {t('portal.messages.archivedView')}
            </Button>
          </div>
        </div>

        <Card>
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : (body?.messages.length ?? 0) === 0 ? (
            <EmptyState
              icon={archivedView ? ArchiveBoxIcon : EnvelopeOpenIcon}
              title={t(
                archivedView
                  ? 'portal.messages.noArchived.title'
                  : 'portal.messages.noMessages.title',
              )}
              description={t(
                archivedView
                  ? 'portal.messages.noArchived.desc'
                  : 'portal.messages.noMessages.desc',
              )}
            />
          ) : (
            <CardRows>
              {(body?.messages ?? []).map((m) => (
                <CardRow key={m.id}>
                  <button
                    type="button"
                    data-testid="portal-message-row"
                    onClick={() => open(m)}
                    className={cn(
                      'hover:bg-accent flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                      // OLVASATLAN: brand-jelölő a bal szélen (a jegy-listával azonos nyelv).
                      m.unread &&
                        'before:bg-brand relative before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[2.5px] before:rounded-full before:content-[""]',
                    )}
                  >
                    <span className="w-4 shrink-0 pt-0.5">
                      {m.unread && <EnvelopeIcon className="text-danger-600 size-4" />}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <Text
                          variant="itemTitle"
                          as="span"
                          className={cn('truncate', m.unread && 'font-semibold')}
                        >
                          {m.subject}
                        </Text>
                        {/* A HATÓKÖR-jelvény a KÖZÖS, IKONOS komponensből (a panel ugyanezt használja). */}
                        <MessageScopeBadge
                          scope={m.scope}
                          label={t(`portal.messages.scope.${m.scope}`, { defaultValue: m.scope })}
                        />
                      </span>
                      <Text variant="secondary" as="span" className="truncate">
                        {m.preview}
                      </Text>
                      <span className="text-muted-foreground flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="truncate">{m.from ?? '—'}</span>
                        {/* A KÖZÖS `MetaDot`: a pont a két adat KÖZÖTT áll. Korábban a `·` a második span
                            ELEJÉN volt, tehát a `gap-2` térköz a pont ELŐTT nyílt, utána viszont csak egy
                            szóköz maradt — a pont látszólag az „x nappal ezelőtt"-hez tartozott. */}
                        <MetaDot />
                        <span>{formatRelativeTime(m.sentAt, i18n.language)}</span>
                        {m.attachmentCount > 0 && (
                          <span className="flex items-center gap-1">
                            <PaperClipIcon className="size-3" />
                            {m.attachmentCount}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </CardRow>
              ))}
            </CardRows>
          )}
        </Card>
      </section>

      {/* A MEGNYITOTT ÜZENET */}
      <Sheet open={openId !== null} onOpenChange={(o) => !o && close()}>
        <SheetContent
          side="right"
          // MOBILON TELJES SZÉLESSÉG, deszktopon fix panel — a repo beúszó-geometriája (`FormSheet`).
          className="flex flex-col p-0"
        >
          {/* A FEJLÉC alatti vonal BEHÚZOTT hajszálvonal (`inset-x-4`), nem szélig futó `border-b`
              (user-lelet): a rendszer hajszálvonal-idiómája ez (`CardRows`, `DataTable`, komment-sáv) —
              a panel széléig futó vonal itt kirajzolta a panel élét, és elvágta a lekerekített sarkot. */}
          <div className="relative shrink-0 p-4 pr-12">
            <SheetTitle>{detail?.subject ?? ''}</SheetTitle>
            <span
              aria-hidden
              className="absolute inset-x-4 bottom-0 h-px bg-[color:var(--table-hairline)]"
            />
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {/* HIBA a panelben (pl. átmeneti fojtás): „Újra" — korábban a betöltés-jelző örökké pörgött, és a
                panel akciói (archiválás / visszaállítás) meg sem jelentek */}
            {detailFailed ? (
              <PortalErrorState onRetry={() => void detailQ.refetch()} />
            ) : detailQ.isLoading || detail === undefined ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <Avatar
                    name={detail.from ?? '—'}
                    src={detail.fromAvatarUrl}
                    accentColor={detail.fromTeamColor}
                    size="lg"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <Text variant="itemTitle" as="span">
                      {detail.from ?? '—'}
                    </Text>
                    <Text variant="secondary" as="span">
                      {formatDateTime(detail.sentAt, i18n.language)}
                    </Text>
                  </div>
                  <MessageScopeBadge
                    scope={detail.scope}
                    label={t(`portal.messages.scope.${detail.scope}`, {
                      defaultValue: detail.scope,
                    })}
                  />
                </div>

                {/* ARCHIVÁLÁS / VISSZAÁLLÍTÁS — az épp látott nézet szerint (a saját listából tünteti el / hozza vissza) */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={archive.isPending}
                    onClick={() => archive.mutate({ id: detail.id, archived: !archivedView })}
                  >
                    {archivedView ? <ArchiveBoxXMarkIcon /> : <ArchiveBoxArrowDownIcon />}
                    {archivedView ? t('portal.messages.unarchive') : t('portal.messages.archive')}
                  </Button>
                </div>

                {/* A törzs SZANITÁLVA jön a szerverről, és a közös komponens ÚJRA sanitizál (két fal). */}
                <RichTextContent html={detail.body} />

                {detail.attachments.length > 0 && (
                  <div className="space-y-2">
                    <Text variant="overline" as="div">
                      {t('portal.tickets.attachments')}
                    </Text>
                    {detail.attachments.map((a) =>
                      a.downloadUrl !== null ? (
                        <a
                          key={a.id}
                          href={a.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={a.filename}
                          className="border-border bg-card hover:bg-accent flex items-center gap-2.5 rounded-[9px] border px-3 py-2 text-[12.5px] transition-colors"
                        >
                          <PaperClipIcon className="text-muted-foreground size-4" />
                          <span className="flex-1 truncate">{a.filename}</span>
                        </a>
                      ) : (
                        <div
                          key={a.id}
                          className="border-border flex items-center gap-2.5 rounded-[9px] border px-3 py-2 text-[12.5px]"
                        >
                          <PaperClipIcon className="text-muted-foreground size-4" />
                          <span className="flex-1 truncate">{a.filename}</span>
                          <Text variant="secondary" as="span">
                            {t('portal.tickets.downloadUnavailable')}
                          </Text>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {/* NINCS LÁBLÉC (user-döntés): az üzenetből NEM nyitunk jegyet — a kettő két külön folyamat, és a
              „Jegyet nyitok ebben" gomb az üzenet olvasása közben félrevezető volt (a jegy amúgy is üres
              űrlapon indult, az üzenetre hivatkozás nélkül). Bezárni az „X"-szel vagy a panelen kívülre
              kattintva lehet — ez a rendszer minden beúszó paneljének a viselkedése, tehát a „Bezárás"
              gombra sincs szükség. */}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Egy meghívó-kártya: dátum-kapszula, cím, idő/hely, szervező, és a VÁLASZ (mindig módosítható). */
function MeetingCard({
  meeting,
  busy,
  onRsvp,
}: {
  meeting: PortalMeetingRow;
  busy: boolean;
  onRsvp: (status: 'pending' | 'confirmed' | 'declined') => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const start = new Date(meeting.startsAt);
  const locale = i18n.language.toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU';

  return (
    <Card className="flex flex-col">
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="bg-violet-soft text-violet flex size-[38px] shrink-0 flex-col items-center justify-center rounded-xl leading-none">
            <span className="text-[9px] font-semibold uppercase">
              {start.toLocaleDateString(locale, { month: 'short' }).replace('.', '')}
            </span>
            <span className="text-[15px] font-semibold">{start.getDate()}</span>
          </span>
          <MeetingRsvpBadge rsvp={meeting.rsvp} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Text variant="title" as="span" className="text-[14.5px] leading-snug">
            {meeting.title}
          </Text>
          <Text variant="secondary" as="span">
            {formatDateTime(meeting.startsAt, i18n.language)}
            {meeting.durationMinutes !== null &&
              ` · ${t('portal.messages.minutes', { count: meeting.durationMinutes })}`}
          </Text>
          {/* NINCS HELYSZÍN-SOR (user-döntés): a MORGENS megbeszélései kivétel nélkül online mennek, a kötelező
              `locationKey` pedig csak BELSŐ irodai teremfoglalás (melyik tárgyalóból ülünk be — `meetings.md` §1),
              ami a partnernek nem releváns. */}
        </div>

        {meeting.organizerName !== null && (
          <div className="mt-auto flex min-w-0 items-center gap-1.5">
            <Text variant="secondary" as="span" className="text-[11px]">
              {t('portal.messages.organizer')}
            </Text>
            {/* A NÉV a meta-sor méretét veszi fel (user-lelet): a `PersonChip` a KÖRNYEZŐ tipográfiát
                örökli, itt pedig a kártya alap-mérete (14px) volt a szülő — a szervező neve így nagyobb
                volt, mint a fölötte álló dátum, és „kiabált" a kis kártyán. */}
            <PersonChip
              name={meeting.organizerName}
              avatarUrl={meeting.organizerAvatarUrl}
              accentColor={meeting.organizerTeamColor}
              size="sm"
              className="text-muted-foreground text-[11px]"
            />
          </div>
        )}

        {meeting.rsvp === 'pending' ? (
          <div className="flex gap-2">
            <Button
              variant="brand"
              size="sm"
              className="flex-1"
              disabled={busy}
              onClick={() => onRsvp('confirmed')}
            >
              <CheckIcon />
              {t('portal.messages.willAttend')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={busy}
              onClick={() => onRsvp('declined')}
            >
              <XMarkIcon />
              {t('portal.messages.wontAttend')}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRsvp('pending')}>
            {t('portal.messages.changeAnswer')}
          </Button>
        )}
      </div>
    </Card>
  );
}
